import { useState } from "react";
import { toast } from "sonner";

import {
  forgetUpload,
  subscribeUpload,
  subscribeUploadSessionClear,
  type UploadSnapshot,
} from "~/resource/data-source/upload-payloads";

import { UploadOperationControls } from "./upload-operation-controls";
import { retainUploadRetry } from "./upload-retry";

export function showUploadOperation(
  operationId: string,
  name: string,
  retry?: () => Promise<unknown>,
  options: { cancellationBestEffort?: boolean } = {},
) {
  const operation: VisibleOperation = operations.get(operationId) ?? { snapshot: { state: "pending" } };
  operation.unsubscribeEligibility?.();
  operation.unsubscribeEligibility = undefined;
  operation.retry = undefined;
  operation.cancellationBestEffort = options.cancellationBestEffort;
  operations.set(operationId, operation);
  observeOperation(operationId, name, retry);
  renderOperation(operationId, name);
}

type VisibleOperation = {
  snapshot: UploadSnapshot;
  retry?: () => Promise<unknown>;
  unsubscribeEligibility?: () => void;
  cancellationBestEffort?: boolean;
};
const operations = new Map<string, VisibleOperation>();
window.addEventListener("pagehide", () => operations.clear());
subscribeUploadSessionClear(() => {
  operations.forEach((_operation, id) => toast.dismiss(id));
  operations.clear();
});

function renderOperation(operationId: string, name: string) {
  const operation = operations.get(operationId);
  if (!operation) return;
  toast.custom(
    () => <UploadToast operationId={operationId} name={name} operation={operation} snapshot={operation.snapshot} />,
    { id: operationId, duration: Infinity, dismissible: false },
  );
}

function UploadToast({
  operationId,
  name,
  operation,
  snapshot,
}: {
  operationId: string;
  name: string;
  operation: VisibleOperation;
  snapshot: UploadSnapshot;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const active = snapshot.state === "pending" || snapshot.state === "uploading";
  return (
    <div className="bg-popover text-popover-foreground border-border w-80 rounded-lg border p-3 shadow-lg">
      <p className="truncate text-sm font-medium">{name}</p>
      {!collapsed && <UploadOperationControls operationId={operationId} snapshot={snapshot} retry={operation.retry} />}
      {active && operation.cancellationBestEffort && (
        <p className="text-muted-foreground text-xs">
          Cancellation is best-effort. A save already accepted by the server may still complete.
        </p>
      )}
      <button
        type="button"
        className="hover:bg-accent min-h-10 rounded px-2 text-xs"
        onClick={() => setCollapsed(!collapsed)}
      >
        {collapsed ? "Expand upload" : "Collapse upload"}
      </button>
      {active ? (
        <p className="text-muted-foreground text-xs">
          Upload continues while collapsed. Expand to view progress or cancel.
        </p>
      ) : (
        <button
          type="button"
          className="hover:bg-accent min-h-10 rounded px-2 text-xs"
          onClick={() => {
            toast.dismiss(operationId);
            operations.delete(operationId);
            forgetUpload(operationId);
          }}
        >
          Dismiss upload
        </button>
      )}
    </div>
  );
}

function observeOperation(operationId: string, name: string, retry?: () => Promise<unknown>) {
  subscribeUpload(operationId, (snapshot: UploadSnapshot) => {
    const operation = operations.get(operationId);
    if (!operation) return;
    operation.snapshot = snapshot;
    const terminal = snapshot.state === "success" || snapshot.state === "error" || snapshot.state === "cancelled";
    if (terminal && retry) {
      operation.unsubscribeEligibility?.();
      operation.unsubscribeEligibility = retainUploadRetry(
        operationId,
        resumeOperation(operationId, name, retry),
        (retained) => {
          if (operations.get(operationId) !== operation) return;
          operation.retry = retained;
          if (operation.snapshot.state === "error") {
            operation.snapshot = { state: "error", canResume: Boolean(retained) };
          }
          renderOperation(operationId, name);
        },
      );
    }
    if (terminal && !snapshot.canResume) operation.retry = undefined;
    if (terminal && !snapshot.canResume) queueMicrotask(() => forgetUpload(operationId));
    renderOperation(operationId, name);
  });
}

function resumeOperation(operationId: string, name: string, retry: () => Promise<unknown>) {
  return async () => {
    observeOperation(operationId, name, retry);
    return retry();
  };
}
